import { HttpStatus } from '@/errors/http-status';
import { useCloneAgent } from '@/pages/agents/api';
import { AgentShareModal, DeleteAgentModal, DeleteEmbedAgentModal, UpgradeV2Modal } from '@/pages/agents/components/modals';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { AgentPageMode } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import { defaultErrorHandler } from '@/utils/default-error-handler';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { setKeyInUserLocalStorage } from '../../utils';
import { Header, Library } from './library';

const Container = styled.div`
    background-color: ${({ theme }) => theme.colors.secondary};
    height: 100%;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
`;

export const AgentsPage = observer(() => {
    const agentEditorStore = useAgentEditorStore();
    const mixpanel = useAnalytics();
    const { user } = useAuth();
    const { mutateAsync: migrate } = useCloneAgent();

    const [itemToShare, setItemToShare] = useState<IAgent>();
    const [itemToDelete, setItemToDelete] = useState<IAgent | null>(null);
    const [agentToUpgrade, setAgentToUpgrade] = useState<IAgent>();
    const [itemToRemoveEmbed, setItemToRemoveEmbed] = useState<IAgent | null>(null);
    const [isOpenModal, setEmbedModalState] = useState(false);

    useEffect(() => {
        agentEditorStore.setPageMode(AgentPageMode.Gallery);
    }, [agentEditorStore.pageMode]);

    const onSubmitV2Modal = useCallback(
        async (disableModal: boolean) => {
            if (agentToUpgrade?.id) {
                if (disableModal) {
                    mixpanel?.track('agents-upgrade-v2', { event: 'dont-show-again' });
                    setKeyInUserLocalStorage(user?.email, 'dontShowUpgradeV2Modal');
                }

                await migrate(agentToUpgrade?.id);
                setAgentToUpgrade(undefined);
            }
        },
        [agentToUpgrade, migrate, mixpanel, user]
    );

    return (
        <Container>
            <Header />

            <Library
                onDelete={(item: IAgent) => setItemToDelete(item)}
                onShare={(item: IAgent) => setItemToShare(item)}
                onRemoveEmbed={(item: IAgent) => setItemToRemoveEmbed(item)}
                onAgentUpgrade={(item: IAgent) => setAgentToUpgrade(item)}
                isOpenModal={isOpenModal}
                setEmbedModalState={setEmbedModalState}
            />

            <DeleteEmbedAgentModal itemToDelete={itemToRemoveEmbed} onClose={() => setItemToRemoveEmbed(null)} />
            <DeleteAgentModal itemToDelete={itemToDelete} onClose={() => setItemToDelete(null)} />
            <UpgradeV2Modal isOpen={!!agentToUpgrade} onClose={() => setAgentToUpgrade(undefined)} onSubmit={onSubmitV2Modal} />
            <AgentShareModal
                agent={itemToShare}
                open={!!itemToShare}
                onClose={() => setItemToShare(undefined)}
                errorHandler={async error => {
                    if (error?.response?.status === HttpStatus.MAX_EMBED_AGENTS_LIMIT_REACHED) {
                        console.warn('Max embed agents limit reached');
                        setEmbedModalState(true);
                        setItemToShare(undefined);
                    } else {
                        defaultErrorHandler(error);
                    }
                }}
            />
        </Container>
    );
});
